<div class="min-h-dvh flex flex-col">
  <header class="h-16 flex items-center justify-between px-6 bg-surface-card border-b border-surface-border">
    <div class="flex gap-4">
      <img
        ngSrc="/logo/velocity-header-logo.png"
        alt="Velocity One Logo"
        width="118"
        height="24"
        priority
      />
      <h1 class="text-md text-[#334155]">Velocity One Identity</h1>
    </div>
    <div class="flex gap-8">
      <!-- Language switcher -->
      <div class="flex items-center gap-1">
        @for (lang of languages; track lang.code) {
          <button
            (click)="setLanguage(lang.code)"
            [class.text-primary-600]="currentLang === lang.code"
            [class.font-semibold]="currentLang === lang.code"
            class="text-xs text-surface-500 hover:text-primary-600 px-1 transition-colors"
          >
            {{ lang.code.toUpperCase() }}
          </button>
          @if (!$last) {
            <span class="text-surface-300 text-xs">|</span>
          }
        }
      </div>

      <!-- Profile -->
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="profile-summary-button"
          (click)="navigateToUserProfile()"
          [attr.aria-label]="'NAV.USER_PROFILE_ARIA_LABEL' | translate: { defaultText: 'Open user profile' }"
        >
          <div class="text-right">
            <p class="text-sm font-medium text-surface-700 leading-tight">
              {{ profileName$ | async }}
            </p>
            <p class="text-xs text-surface-400 leading-tight capitalize">
              {{ profileRole$ | async }}
            </p>
          </div>
          <div class="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center select-none">
            <span class="text-primary-600 text-xs font-semibold">
              {{ (profileName$ | async)?.charAt(0)?.toUpperCase() ?? '' }}
            </span>
          </div>
        </button>
        <button
          type="button"
          class="logout-icon-button"
          (click)="logout()"
          aria-label="Log out"
          title="Log out"
        >
          <i class="pi pi-sign-out" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </header>

  <p-dialog
    [visible]="showLogoutConfirm()"
    (visibleChange)="showLogoutConfirm.set($event)"
    [modal]="true"
    [dismissableMask]="true"
    [draggable]="false"
    [resizable]="false"
    [closable]="false"
    styleClass="logout-confirmation-dialog"
    [header]="'NAV.LOGOUT_CONFIRM_TITLE' | translate: { defaultText: 'Sign out?' }"
  >
    <p class="logout-confirmation-text">{{ 'NAV.LOGOUT_CONFIRM_BODY' | translate: { defaultText: 'You are about to end your current session.' } }}</p>
    <ng-template #footer>
      <div class="logout-confirmation-actions">
        <p-button
          [label]="'COMMON.CANCEL' | translate: { defaultText: 'Cancel' }"
          [outlined]="true"
          severity="secondary"
          (click)="cancelLogout()"
        />
        <p-button
          [label]="'AUTH.CONTINUE' | translate: { defaultText: 'Continue' }"
          severity="danger"
          (click)="confirmLogout()"
        />
      </div>
    </ng-template>
  </p-dialog>

  <div class="flex flex-1 min-h-0 overflow-hidden bg-surface-ground p-[12px] bg-[#E2E8F0]">
    <!-- Sidebar -->
    <aside class="w-64 flex-shrink-0 flex flex-col min-h-0 bg-surface-card border-r border-surface-border">
      <!-- Logo / Brand -->
      <div class="px-6 pt-[10px]">
        <span class="text-[12px]">{{ 'NAV.WORKSPACE_TITLE' | translate: { defaultText: 'Current Workspace' } }}</span>
      </div>
      <div class="h-16 flex items-center border-b border-surface-border">
        <div class="w-[100%] px-[16px] py-[12px] bg-[#0C4A6E] rounded-[8px]">
          <span class="text-[14px] font-semibold text-white">Velocity Suite</span>
        </div>
      </div>

      <div class="px-6 py-[10px]">
        <span class="text-[12px]">{{ 'NAV.MANAGE_LABEL' | translate: { defaultText: 'Manage' } }}</span>
      </div>
      <!-- Navigation -->
      <div>
        <nav class="flex-1 overflow-y-auto py-[10px] rounded-[8px] space-y-1 bg-white">
          <a
            routerLink="/admin/identity-providers"
            routerLinkActive="bg-primary-50 text-primary-700 font-semibold"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100 transition-colors"
          >
            {{ 'NAV.IDENTITY_PROVIDERS' | translate: { defaultText: 'Identity Providers' } }}
          </a>
          <a
            routerLink="/admin/organizations"
            routerLinkActive="bg-primary-50 text-primary-700 font-semibold"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100 transition-colors"
          >
            {{ 'NAV.ORGANIZATIONS' | translate: { defaultText: 'Organizations' } }}
          </a>
          <a
            routerLink="/admin/applications"
            routerLinkActive="bg-primary-50 text-primary-700 font-semibold"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100 transition-colors"
          >
            {{ 'NAV.APPLICATIONS' | translate: { defaultText: 'Applications' } }}
          </a>
          <a
            routerLink="/admin/users"
            routerLinkActive="bg-primary-50 text-primary-700 font-semibold"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100 transition-colors"
          >
            {{ 'NAV.USERS' | translate: { defaultText: 'Users' } }}
          </a>
          <a
            routerLink="/admin/roles"
            routerLinkActive="bg-primary-50 text-primary-700 font-semibold"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100 transition-colors"
          >
            {{ 'NAV.ROLES' | translate: { defaultText: 'Roles' } }}
          </a>
          <a
            routerLink="/admin/sessions"
            routerLinkActive="bg-primary-50 text-primary-700 font-semibold"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100 transition-colors"
          >
            {{ 'NAV.SESSIONS' | translate: { defaultText: 'Sessions' } }}
          </a>
          <a
            routerLink="/admin/settings"
            routerLinkActive="bg-primary-50 text-primary-700 font-semibold"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100 transition-colors"
          >
            {{ 'NAV.SETTINGS' | translate: { defaultText: 'Settings' } }}
          </a>
        </nav>
      </div>
      
    </aside>

    <!-- Main column: header + content -->
    <div class="flex flex-col flex-1 min-h-0 overflow-hidden bg-white mx-[12px] rounded-[8px] border border-[#CBD5E1]">

      <!-- Top header -->
      <header class="h-16 flex-shrink-0 flex items-center justify-between px-6 bg-surface-card border-b border-surface-border">
        <div class="text-sm text-surface-500">
          <p-breadcrumb [model]="breadcrumbs()" styleClass="border-none bg-transparent p-0 text-sm" />
        </div>
      </header>

      <!-- Scrollable content area -->
      <main class="flex-1 overflow-y-auto p-6">
        <router-outlet />
      </main>

    </div>
  </div>
</div>

